import type { ReactNode } from "react";
import { Icon } from "./icons";
import { downloadFile } from "../api/client";
import { toast } from "./toast";
import type { Assignment } from "../api/types";

const pad = (n: number) => String(n).padStart(2, "0");
const runtime = (s: number | null) => {
  if (!s) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};
const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—";

const STATUS: Record<string, { label: string; cls: string }> = {
  assigned: { label: "To do", cls: "pl-todo" },
  in_progress: { label: "In progress", cls: "pl-prog" },
  submitted: { label: "Complete", cls: "pl-sub" },
  delivered: { label: "Delivered", cls: "pl-ok" },
  invoiced: { label: "Invoiced", cls: "pl-inv" },
  settled: { label: "Settled", cls: "pl-set" },
};

// What the proofer may set. Delivered stays the admin's call, so "Complete"
// reads as "my part's done", not "accepted".
const MY_STATUSES = ["assigned", "in_progress", "submitted"];

function copyPw(text: string) {
  navigator.clipboard?.writeText(text).then(
    () => toast("Password copied", "success"),
    () => toast("Couldn't copy", "error"),
  );
}
const openUrl = (u: string) => { if (u) window.open(u, "_blank", "noopener"); };

/**
 * The Proofer's Log as a spreadsheet — ONE line per project, mirroring the Excel
 * log it replaces (Title · Script · Frame · Pass · Runtime · status). Shared by
 * the proofer's My Work and the admin's read-only view of it, so both read the
 * same way. `onStatus` makes the Status cell editable (the proofer moves their
 * own row); `action` injects the admin's control (e.g. Confirm delivered).
 */
export function AssignmentTable({
  items, onStatus, action, showProofer = false,
}: {
  items: Assignment[];
  /** Present ⇒ the caller owns these rows and may move them (the proofer). */
  onStatus?: (id: string, status: string) => void;
  action?: (a: Assignment) => ReactNode;
  showProofer?: boolean;
}) {
  return (
    <div className="pl-tablewrap">
      <table className="pl-table">
        <thead>
          <tr>
            <th>Title</th>
            {showProofer && <th>Proofer</th>}
            <th>Scope</th>
            <th>Service</th>
            <th>Script</th>
            <th>Frame</th>
            <th>Pass</th>
            <th className="num">Runtime</th>
            <th>Delivered</th>
            <th>Status</th>
            {action && <th />}
          </tr>
        </thead>
        <tbody>
          {items.map((a) => {
            const e = a.essentials;
            const st = STATUS[a.status] ?? STATUS.assigned;
            return (
              <tr key={a.id}>
                <td className="pl-t-title">{a.project_name ?? "—"}</td>
                {showProofer && <td>{a.proofer_name ?? "—"}</td>}
                <td className="pl-t-scope">{a.scope_label || "whole book"}</td>
                <td>{a.service || "—"}</td>
                <td>
                  {e?.manuscript ? (
                    <button className="pl-t-ico" title="Download the manuscript"
                      onClick={() => downloadFile(`/api/projects/${e.project_id}/manuscript.pdf?download=1`)}>
                      <Icon name="file-text" size={14} />
                    </button>
                  ) : <span className="pl-t-none">—</span>}
                </td>
                <td>
                  {e?.review_link ? (
                    <button className="pl-t-ico" title="Open the review link" onClick={() => openUrl(e.review_link)}>
                      <Icon name="external" size={14} />
                    </button>
                  ) : <span className="pl-t-none">—</span>}
                </td>
                <td>
                  {e?.review_pw ? (
                    <button className="pl-t-ico" title="Copy the review password" onClick={() => copyPw(e.review_pw)}>
                      <Icon name="copy" size={14} />
                    </button>
                  ) : <span className="pl-t-none">—</span>}
                </td>
                <td className="num">{runtime(a.runtime_seconds)}</td>
                <td className="pl-t-date">{day(a.delivered_at)}</td>
                <td>
                  {onStatus && MY_STATUSES.includes(a.status) ? (
                    <select className={`pl-statussel ${st.cls}`} value={a.status}
                      onChange={(e) => onStatus(a.id, e.target.value)}>
                      {MY_STATUSES.map((s2) => (
                        <option key={s2} value={s2}>{STATUS[s2].label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`pl-pill ${st.cls}`}>{st.label}</span>
                  )}
                </td>
                {action && <td className="pl-t-act">{action(a)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
